import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import logoOrange from "../../assets/static/ALOHAR_LOGO_naranja.png";
import searchIcon from "../../assets/static/Landing/icon/search.svg";
import { BASE_URL, loadUserData } from "../../Helpers";
import SelectCity from "../MainSearch/SelectCity";
import SelectDate from "../MainSearch/SelectDate";
import LanguageDropdown from "../ui/languageDropdown";
import { useLanguage } from "../../store/lenguaje/useLenguaje";
import arg from "../../assets/static/flags/arg.png";
import po from "../../assets/static/flags/br.png";
import eeuu from "../../assets/static/flags/eeuu.png";

const HomeNavbar = ({ threshold = 100 }) => {
  const history = useHistory();
  const containerRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const searchReducer = useSelector((state) => state.searchReducer);
  const [searchData, setSearchData] = useState({
    checkin: "",
    checkout: "",
    guests: "2",
  });

  const userData = loadUserData();

  const languages = [
    { code: "es", name: "Español", flag: arg },
    { code: "po", name: "Portugués", flag: po },
    { code: "en", name: "Inglés", flag: eeuu },
  ];
  const { setCurrentLang, currentLang } = useLanguage();

  const avatarUrl = useMemo(() => {
    const defaultAvatar =
      "https://ui-avatars.com/api/?size=256&font-size=0.33&background=FF7D52&color=fff";

    if (!userData) {
      return `${defaultAvatar}&name=Invitado`;
    }

    const { avatar, firstName } = userData;
    if (avatar && firstName) {
      return avatar.includes("http") ? avatar : `${BASE_URL}/${avatar}`;
    }

    return `${defaultAvatar}&name=${firstName || "Invitado"}`;
  }, [userData]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    const onResize = () => setIsMobileView(window.innerWidth < 768);

    onScroll();
    window.addEventListener("scroll", onScroll);
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, [threshold]);

  useEffect(() => {
    if (!scrolled) {
      setSearchExpanded(false);
    }
  }, [scrolled]);

  useEffect(() => {
    const onEscape = (event) => {
      if (event.key === "Escape") {
        setSearchExpanded(false);
      }
    };

    const onClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setSearchExpanded(false);
      }
    };

    if (searchExpanded) {
      document.addEventListener("keydown", onEscape);
      document.addEventListener("mousedown", onClickOutside);
    }

    return () => {
      document.removeEventListener("keydown", onEscape);
      document.removeEventListener("mousedown", onClickOutside);
    };
  }, [searchExpanded]);

  const handleSearch = () => {
    const { guests } = searchData;
    const cityName = searchReducer?.cityName || "";
    const cityNameSlug = cityName.replace(/\s+/g, "-");
    const checkin = searchReducer?.checkin || "";
    const checkout = searchReducer?.checkout || "";
    const neighborhood = searchReducer?.neighborhood;
    const query = new URLSearchParams({
      city: searchReducer?.city || "",
      checkin,
      checkout,
      childs: "0",
      adults: guests || "2",
      cityName,
    });

    if (neighborhood && neighborhood !== "false") {
      query.set("locationNeighborhood", neighborhood);
    }

    history.push(`/${cityNameSlug}/search?${query.toString()}`);
    setSearchExpanded(false);
  };

  const preSearch = () => {
    const hasCity = Boolean(searchReducer?.city && searchReducer?.cityName);
    const hasDates = Boolean(searchReducer?.checkin && searchReducer?.checkout);

    if (!hasCity || !hasDates) {
      setSearchExpanded(true);
      return;
    }

    handleSearch();
  };

  const compactSearchBar = (
    <motion.button
      type="button"
      layoutId="sticky-destination-pill"
      onClick={() => setSearchExpanded(true)}
      className="flex w-full max-w-[360px] items-center justify-between gap-3 rounded-full border border-black/10 bg-white px-5 py-2 shadow-[0_6px_18px_rgba(15,23,42,0.08)]"
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.995 }}
    >
      <div className="flex min-w-0 items-center gap-2 text-[14px] text-black/75">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="text-[#FF4E00]">
          <path d="M21 21L16.6 16.6M18 11C18 14.866 14.866 18 11 18C7.13401 18 4 14.866 4 11C4 7.13401 7.13401 4 11 4C14.866 4 18 7.13401 18 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span className="truncate font-semibold text-black">¿Dónde vamos?</span>
      </div>
      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[#FF4E00] text-[11px] font-bold text-white">
        GO
      </span>
    </motion.button>
  );

  const desktopSearchGroup = (
    <div className="flex min-w-0 flex-1 justify-center overflow-visible px-4">
      <motion.div
        layout
        className="min-w-0 overflow-visible"
        animate={{
          maxWidth: searchExpanded ? 760 : 360,
          x: searchExpanded ? -10 : 0,
        }}
        transition={{ type: "spring", stiffness: 320, damping: 34 }}
        style={{
          width: searchExpanded ? "100%" : 360,
          maxWidth: searchExpanded ? 760 : 360,
        }}
      >
        <AnimatePresence mode="wait" initial={false}>
          {searchExpanded ? (
            <motion.div
              key="expanded-search"
              layout
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 12 }}
              transition={{ duration: 0.24, ease: "easeOut" }}
              className="flex min-w-0 w-full flex-nowrap items-center gap-3"
              style={{
                width: "100%",
              }}
            >
              <motion.div
                layoutId="sticky-destination-pill"
                className="home-navbar-destination min-w-0 overflow-visible rounded-full border border-black/10 bg-white px-5 py-2 shadow-[0_6px_18px_rgba(15,23,42,0.08)]"
                style={{ position: "relative", height: 52, flex: "1 1 auto", minWidth: 220, maxWidth: 430 }}
              >
                <SelectCity
                  cityName={searchReducer?.cityName}
                  newStyle={true}
                  selectedCity={searchReducer?.city}
                  placeholder={searchReducer?.cityName || "¿Dónde vamos?"}
                />
              </motion.div>

              <motion.div
                layout
                initial={{ opacity: 0, x: 18 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 18 }}
                transition={{ duration: 0.22, ease: "easeOut" }}
                className="home-navbar-guests flex min-w-0 items-center rounded-full border border-black/10 bg-white px-4 shadow-[0_6px_18px_rgba(15,23,42,0.08)]"
                style={{ height: 52, flex: "0 0 120px", minWidth: 120, maxWidth: 120 }}
              >
                <input
                  type="number"
                  min="1"
                  max="8"
                  step="1"
                  value={searchData.guests}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === "") {
                      setSearchData((prev) => ({ ...prev, guests: "1" }));
                      return;
                    }
                    const next = Math.max(1, Math.min(8, Number(raw)));
                    setSearchData((prev) => ({ ...prev, guests: String(next) }));
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      preSearch();
                    }
                  }}
                  aria-label="Huéspedes"
                  placeholder="Huéspedes"
                  className="w-full min-w-0 bg-transparent text-center text-[13px] font-medium text-black outline-none"
                  style={{ border: "none", boxShadow: "none" }}
                />
              </motion.div>

              <motion.div
                layout
                initial={{ opacity: 0, x: 22 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 22 }}
                transition={{ duration: 0.24, ease: "easeOut" }}
                className="home-navbar-dates flex min-w-0 items-center overflow-visible rounded-full border border-black/10 bg-white px-4 shadow-[0_6px_18px_rgba(15,23,42,0.08)]"
                style={{ position: "relative", height: 52, flex: "0 1 290px", minWidth: 240, maxWidth: 320 }}
              >
                <div className="min-w-0 flex-1">
                  <SelectDate
                    checkin={searchReducer?.checkin || ""}
                    checkout={searchReducer?.checkout || ""}
                    newStyle={true}
                  />
                </div>
              </motion.div>

              <motion.button
                layout
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 24 }}
                transition={{ duration: 0.24, ease: "easeOut" }}
                type="button"
                onClick={preSearch}
                aria-label="Buscar"
                className="MainInputSearch__searchButton grid h-[52px] w-[52px] shrink-0 place-items-center rounded-full bg-[#06b6d4] text-white shadow-[0_10px_24px_rgba(6,182,212,0.35)] transition"
                whileHover={{ scale: 1.06, backgroundColor: "#96F9FF", y: -1 }}
                whileTap={{ scale: 0.98 }}
              >
                <img
                  src={searchIcon}
                  alt="Buscar"
                  className="MainInputSearch__searchButton__img h-[70%] w-[70%] object-contain"
                />
              </motion.button>
            </motion.div>
          ) : (
            <motion.div
              key="compact-search"
              layout
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -8 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              {compactSearchBar}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );

  const mobileExpandedContent = (
    <div className="flex w-full flex-col items-stretch gap-3">
      <div
        className="home-navbar-destination rounded-full border border-black/10 bg-white px-4 py-2"
        style={{ minHeight: 0, overflow: "visible", position: "relative" }}
      >
        <SelectCity
          cityName={searchReducer?.cityName}
          newStyle={true}
          selectedCity={searchReducer?.city}
          placeholder={searchReducer?.cityName || "¿Dónde vamos?"}
        />
      </div>
      <div className="home-navbar-guests flex h-[52px] items-center rounded-full border border-black/10 bg-white px-4">
        <input
          type="number"
          min="1"
          max="8"
          step="1"
          value={searchData.guests}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") {
              setSearchData((prev) => ({ ...prev, guests: "1" }));
              return;
            }
            const next = Math.max(1, Math.min(8, Number(raw)));
            setSearchData((prev) => ({ ...prev, guests: String(next) }));
          }}
          aria-label="Huéspedes"
          placeholder="Huéspedes"
          className="w-full bg-transparent text-center text-[14px] font-medium text-black outline-none"
          style={{ border: "none", boxShadow: "none" }}
        />
      </div>
      <div className="home-navbar-dates flex h-[52px] items-center overflow-visible rounded-full border border-black/10 bg-white px-4"
        style={{ position: "relative" }}
      >
        <SelectDate
          checkin={searchReducer?.checkin || ""}
          checkout={searchReducer?.checkout || ""}
          newStyle={true}
        />
      </div>
      <motion.button
        type="button"
        onClick={handleSearch}
        className="grid h-[52px] w-full place-items-center rounded-full bg-[#06b6d4] text-white shadow-[0_10px_24px_rgba(6,182,212,0.35)]"
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
      >
        Buscar
      </motion.button>
    </div>
  );

  return (
    <AnimatePresence>
      {scrolled && (
        <motion.div
          initial={{ opacity: 0, y: -28 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -24 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="fixed left-0 right-0 top-4 z-[120]"
          style={{ position: "fixed", left: 0, right: 0, top: isMobileView ? 0 : 10, zIndex: 220 }}
        >
          {isMobileView ? (
            <div
              style={{
                background: "white",
                padding: "10px 16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
              }}
            >
              <button
                type="button"
                onClick={() => history.push("/")}
                style={{ background: "transparent", border: 0, padding: 0, lineHeight: 0 }}
              >
                <img src={logoOrange} alt="Alohar" style={{ height: "2rem", width: "auto" }} />
              </button>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <LanguageDropdown
                  languages={languages}
                  currentLang={currentLang}
                  setCurrentLang={setCurrentLang}
                />
                <button
                  type="button"
                  onClick={() => history.push(userData ? "/profile" : "/login")}
                  className="usermenubutton padding_0"
                >
                  <div
                    className="usermenubutton__imgcontainer padding_0"
                    style={{ border: "1px solid rgba(0,0,0,0.15)", borderRadius: "50%" }}
                  >
                    <img
                      src={avatarUrl}
                      alt="Usuario"
                      className="usermenubutton__imgcontainer__img"
                      style={{ width: "100%", height: "100%" }}
                    />
                  </div>
                </button>
              </div>
            </div>
          ) : (
          <div
            ref={containerRef}
            className="mx-auto rounded-[24px] border border-white/55 bg-white/85 p-3 backdrop-blur-xl shadow-[0_16px_60px_rgba(15,23,42,0.16)]"
            style={{
              position: "relative",
              width: isMobileView ? "calc(100% - 24px)" : "83%",
              margin: "0 auto",
              borderRadius: 18,
              border: "1px solid rgba(255,255,255,0.12)",
              background: "rgba(45,45,45,0.92)",
              padding: 8,
              boxShadow: "0 16px 60px rgba(15,23,42,0.16)",
            }}
          >
            <style>
              {`
                /* Destino: dropdown flota fuera del navbar */
                .home-navbar-destination .input_wrapperNew {
                  border-radius: 999px;
                  overflow: visible !important;
                  position: relative;
                }
                .home-navbar-destination .input {
                  border-radius: 999px;
                  min-height: 26px;
                  padding-left: 4px;
                  padding-right: 4px;
                  font-size: 0.82rem !important;
                  font-weight: 600 !important;
                  line-height: 1.1 !important;
                  border: none !important;
                  box-shadow: none !important;
                }
                .home-navbar-destination .input::placeholder {
                  font-size: 0.82rem !important;
                  font-weight: 600 !important;
                }
                .home-navbar-destination .z-10 {
                  border-radius: 12px;
                  z-index: 99999 !important;
                  overflow-y: auto;
                  position: absolute !important;
                  top: calc(100% + 8px) !important;
                  left: 0 !important;
                  right: auto !important;
                  min-width: 100% !important;
                  box-shadow: 0 16px 40px rgba(0,0,0,0.2);
                }
                /* Fechas: DateRangePicker compacto y sin bordes propios */
                .home-navbar-dates .DateRangePicker,
                .home-navbar-dates .DateRangePickerInput {
                  background: transparent !important;
                  border: none !important;
                  display: flex;
                  align-items: center;
                  width: 100%;
                }
                .home-navbar-dates .DateInput {
                  background: transparent !important;
                  width: 48% !important;
                }
                .home-navbar-dates .DateInput_input {
                  background: transparent !important;
                  border: none !important;
                  font-size: 0.82rem !important;
                  padding: 0 4px !important;
                  color: #000 !important;
                  font-weight: 600;
                  line-height: 1.2 !important;
                  text-align: center !important;
                }
                .home-navbar-dates .DateInput_input::placeholder {
                  font-size: 0.82rem !important;
                  font-weight: 600 !important;
                  color: #111 !important;
                }
                .home-navbar-dates .DateRangePickerInput_arrow {
                  font-size: 10px;
                  color: rgba(0,0,0,0.35);
                }
                .home-navbar-dates .input_wrapperNew > div > div > div {
                  width: 100% !important;
                  margin: 0 !important;
                  padding: 0 !important;
                  display: flex !important;
                  align-items: center !important;
                  justify-content: space-between !important;
                }
                .home-navbar-dates .input_wrapperNew > div > div > div > div:nth-child(1) > input,
                .home-navbar-dates .input_wrapperNew > div > div > div > div:nth-child(3) > input,
                .home-navbar-dates .input_wrapperNew > div > div > div > div:nth-child(1) > input::placeholder,
                .home-navbar-dates .input_wrapperNew > div > div > div > div:nth-child(3) > input::placeholder {
                  font-size: 0.72rem !important;
                  font-weight: 600 !important;
                  text-align: center !important;
                  line-height: 1 !important;
                }
                .home-navbar-dates .input_wrapperNew > div > div > div > div:nth-child(2) {
                  top: calc(100% + 8px) !important;
                  left: 50% !important;
                  transform: translateX(-50%) !important;
                  border-radius: 14px;
                }
                .home-navbar-dates .DateRangePicker_picker {
                  z-index: 99999 !important;
                  top: calc(100% + 8px) !important;
                  border-radius: 16px;
                  overflow: hidden;
                  box-shadow: 0 16px 40px rgba(0,0,0,0.18);
                }
                .home-navbar-guests input {
                  border: none !important;
                  box-shadow: none !important;
                  outline: none !important;
                  background: transparent !important;
                }
                .home-navbar-guests input::-webkit-outer-spin-button,
                .home-navbar-guests input::-webkit-inner-spin-button {
                  margin: 0;
                }
              `}
            </style>
            {/* Fila 1: siempre visible — Logo | Píldora | Avatar */}
            <div
              className="navbar__container"
              style={{
                position: "relative",
                gap: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              {/* Logo */}
              <button
                type="button"
                onClick={() => history.push("/")}
                style={{
                  flexShrink: 0,
                  background: "transparent",
                  border: 0,
                  padding: 0,
                  lineHeight: 0,
                }}
              >
                <img
                  src={logoOrange}
                  alt="Alohar"
                  className="logo"
                  style={{ height: "2.5rem", width: "auto" }}
                />
              </button>

              {/* Píldora compacta centrada — solo cuando NO expandido */}
              {desktopSearchGroup}

              {/* Avatar */}
              <button
                type="button"
                onClick={() => history.push(userData ? "/profile" : "/login")}
                className="usermenubutton padding_0"
                style={{ flexShrink: 0 }}
              >
                <div
                  className="usermenubutton__imgcontainer padding_0"
                  style={{
                    border: "1px solid rgba(255,255,255,0.35)",
                    borderRadius: "50%",
                  }}
                >
                  <img
                    src={avatarUrl}
                    alt="Usuario"
                    className="usermenubutton__imgcontainer__img"
                    style={{ width: "100%", height: "100%" }}
                  />
                </div>
              </button>
            </div>

            <AnimatePresence>
              {searchExpanded && isMobileView && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="fixed inset-0 z-[130] bg-black/45"
                >
                  <motion.div
                    initial={{ y: "100%" }}
                    animate={{ y: 0 }}
                    exit={{ y: "100%" }}
                    transition={{ duration: 0.28, ease: "easeOut" }}
                    className="absolute bottom-0 left-0 right-0 rounded-t-[24px] bg-white p-4"
                  >
                    <div className="mb-3 flex items-center justify-between">
                      <h3 className="text-base font-semibold">Buscar estadia</h3>
                      <button
                        type="button"
                        onClick={() => setSearchExpanded(false)}
                        className="rounded-full p-2 text-black/60"
                      >
                        ✕
                      </button>
                    </div>
                    {mobileExpandedContent}
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default HomeNavbar;
